import { FieldInfo } from '@/components/FieldInfo';
import type { OrgFormApi } from '@/config/organizations/formTypes';
import { getLocalTimeZone, parseDate, today } from '@internationalized/date';
import { DatePicker } from '@nautilusbio/nb-design-system/DatePicker';
import { Grid } from '@nautilusbio/nb-design-system/Grid';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Switch } from '@nautilusbio/nb-design-system/Switch';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { useStore } from '@tanstack/react-store';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import orgStyles from '../../styles.module.css';
import { gridProps, stackProps } from '../constants';

type SubscriptionSectionProps = {
  form: OrgFormApi;
  isReadOnly?: boolean;
  /** The org's already-persisted expiration date (edit forms only). Floors the calendar's
   *  selectable range at that date instead of today when it's already in the past, so loading
   *  an org with a pre-existing lapsed subscription doesn't render its own current value as
   *  out-of-range. The Zod schema (not this prop) is what actually gates submission. */
  originalExpiration?: string;
  /** Force-show validation errors regardless of per-field touched state (e.g. after a blocked submit attempt). */
  showErrors?: boolean;
};

function SubscriptionSectionComponent({
  form,
  isReadOnly = false,
  originalExpiration,
  showErrors,
}: SubscriptionSectionProps) {
  const { t } = useTranslation('orgs');
  const isActive = useStore(form.baseStore, s => s.values.subscription.active);
  const todayValue = today(getLocalTimeZone());
  const minValue = (() => {
    if (!originalExpiration) return todayValue;
    try {
      const original = parseDate(originalExpiration);
      return original.compare(todayValue) < 0 ? original : todayValue;
    } catch {
      return todayValue;
    }
  })();

  return (
    <Stack {...stackProps} fullHeight>
      <Text as="h3" className={orgStyles.sectionHeading}>
        {t('create_org_section_subscription')}
      </Text>

      <Grid {...gridProps} alignItems="center" fullHeight placeContent="center">
        <form.Field name="subscription.active">
          {field => (
            <>
              <Switch
                checked={field.state.value}
                data-testid="input-subscription-active"
                disabled={isReadOnly}
                id="subscription-is_active"
                label={t('create_org_subscription_active_label')}
                name="subscription-is_active"
                onChange={checked => field.handleChange(checked)}
                onBlur={field.handleBlur}
              />
              <FieldInfo field={field} showErrors={showErrors || field.state.meta.isTouched} />
            </>
          )}
        </form.Field>

        {isActive && (
          <form.Field name="subscription.expiration">
            {field => {
              const datePickerLabel = {
                children: t('create_org_subscription_expiration_label'),
                hasPlaceholder: !field.state.value,
                isFocused: false,
                hasValue: !!field.state.value,
                htmlFor: 'subscription-expires_at',
              };

              return (
                <>
                  <DatePicker
                    data-testid="input-subscription-expiration"
                    id="subscription-expires_at"
                    isReadOnly={isReadOnly}
                    label={datePickerLabel}
                    minValue={minValue}
                    name="subscription-expires_at"
                    onBlur={field.handleBlur}
                    onChange={date => field.handleChange(date ? date.toString() : '')}
                    value={(() => {
                      if (!field.state.value) return null;
                      try {
                        return parseDate(field.state.value);
                      } catch {
                        return null;
                      }
                    })()}
                  />

                  <FieldInfo field={field} showErrors={showErrors || field.state.meta.isTouched} />
                </>
              );
            }}
          </form.Field>
        )}
      </Grid>
    </Stack>
  );
}

export const SubscriptionSection = memo(SubscriptionSectionComponent);
